import type { FC } from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import Loading from '@/components/Loading';
import { SentryUtils } from '@/utils/SentryUtils';
import { DatadogUtils } from '@/utils/Datadog.utils';
import { setGlobalData } from '@/actions/global-data';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import { ApiService } from '@/support/ApiService';
import { TeamThemeService } from '@/support/TeamThemeService';
import type { Team, TeamApiId, TeamTheme } from '@/model/Team.model';
import { _isNil, _notNil, getSessionCorrelationId, jwtClaim, jwtValid } from '@/littledash';
import type { AxiosRequestConfig } from 'axios';
import {
  BenchlingLinkService,
  fetchBenchlingLinkState,
  NOT_LINKED,
  showBenchlingLinkRequiredToast,
} from '@/support/benchlingLink/BenchlingLink.utils';

interface AuthenticatedProps {
  globalData?: boolean;
  children: ReactNode;
}

type AxiosRequestConfigWithRedirect = AxiosRequestConfig & { redirect?: Partial<Record<number, boolean>> };
const NO_REDIRECT_ON_401: AxiosRequestConfigWithRedirect = { redirect: { 401: false } };

const isTeamTheme = (response: TeamTheme | never): response is TeamTheme =>
  _notNil(response?.primary) && _notNil(response?.on_primary);

/**
 * The global-data endpoint requires the team-scoped long-lived token (`llt`).
 * Guards against firing before a valid `llt` is in localStorage. During the
 * login handshake the token is either absent (request goes out with no
 * Authorization header) or still a non-`llt` type such as the short-lived
 * token (request goes out with an Authorization header the endpoint rejects) —
 * both 401, and the 401 redirect bounces the user back to the login flow.
 */
const hasValidLongLivedToken = (): boolean => jwtValid() && jwtClaim('type') === 'llt';

/** Once per browser session per team, so a lapsed link is raised on arrival without nagging on every load. */
const notifyLinkExpiredOnce = (teamApiId: TeamApiId): void => {
  const key = `benchling-link-expired-notified:${teamApiId}`;
  if (sessionStorage.getItem(key) !== null) {
    return;
  }
  sessionStorage.setItem(key, 'true');
  showBenchlingLinkRequiredToast(true);
};

/**
 * Loads the user's Benchling link after the app has rendered rather than with it. Only delegated
 * actions read the link and none is reachable in the first moments of a load, so startup never waits
 * on a check that may be rotating tokens with Benchling.
 */
const loadBenchlingLink = (teamApiId: TeamApiId): void => {
  const since = BenchlingLinkService.version();
  fetchBenchlingLinkState().then((state) => {
    // Anything that learned the link while this check ran knows better than the check.
    if (!BenchlingLinkService.setIfUnchangedSince(since, state)) {
      return;
    }
    if (state.status === 'LINK_EXPIRED') {
      notifyLinkExpiredOnce(teamApiId);
    }
  });
};

const Authenticated: FC<AuthenticatedProps> = ({ children, globalData = true }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [_, setError] = useState<Error | false>(false);
  const { newAbortController: newSessionDetailAbortController } = useAbortController();
  const { newAbortController: newGlobalDataAbortController } = useAbortController();

  const dispatch = useDispatch();
  const { team } = useSelector<{ team: { team: Team } }, { team: Team }>(({ team: { team } }) => ({ team }));

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const { data } = await Http.get(apiRoute('sessions.get-details'), {
        signal: newSessionDetailAbortController().signal,
      });
      dispatch({
        type: 'USER_SET_CURRENT_USER',
        currentUser: data.user,
        teams: data.teams,
      });
      setLoading(false);
    } catch (error) {
      if (notAborted(error)) {
        setError(error as Error);
        setLoading(false);
      }
    }
  };

  const fetchGlobalData = async () => {
    try {
      const signal = newGlobalDataAbortController().signal;
      // The team is unset while this runs, so nothing reads the link; clearing it here means a team
      // switch cannot carry the previous team's link over, whether or not the new team has the feature.
      BenchlingLinkService.set(NOT_LINKED);
      const [globalDataResult, teamThemeResult] = await Promise.allSettled([
        Http.get(apiRoute('global-data'), { signal }),
        ApiService.call({
          endpoint: 'GET /api/v1/team/theme',
          query: { s: jwtClaim('jti') ?? getSessionCorrelationId() },
          signal,
          options: {
            onError: { slug: 'team-theme-fetch', toast: false, throw: false, capture: true },
            requestConfigOverrides: NO_REDIRECT_ON_401,
          },
        }),
      ]);

      if (globalDataResult.status === 'rejected') {
        throw globalDataResult.reason;
      }
      const { data } = globalDataResult.value;

      if (teamThemeResult.status === 'fulfilled') {
        const teamThemeResponse = teamThemeResult.value;
        if (teamThemeResponse.type === 'success' && isTeamTheme(teamThemeResponse.body)) {
          TeamThemeService.apply(teamThemeResponse.body);
        } else {
          TeamThemeService.clear();
        }
      }

      setGlobalData(data, dispatch);
      const teamApiId = jwtClaim('team_id') as TeamApiId;
      if (data.features?.benchling_delegated_auth === true) {
        loadBenchlingLink(teamApiId);
      }
      SentryUtils.setUser(
        data.currentUser.api_id,
        data.currentUser.email,
        teamApiId,
        data.team.name,
        data.team.subdomain
      );
      DatadogUtils.setUser(
        data.currentUser.api_id,
        data.currentUser.email,
        teamApiId,
        data.team.name,
        data.team.subdomain
      );
    } catch (error) {
      if (notAborted(error)) {
        setError(error as Error);
      }
    }
  };

  useEffect(() => {
    if (globalData) {
      if (_isNil(team) && hasValidLongLivedToken()) {
        fetchGlobalData();
      }
    } else {
      fetchUserData();
    }
  }, [globalData, team]);

  return <div className="h-100 flex-grow-1 w-100">{!loading ? children : <Loading />}</div>;
};

export default Authenticated;
